/**
 * stock-availability-engine.js
 * ===================================
 * Version: 1.1.1
 * Date: 2024-12-31
 * Author: Nicholas Rathwell
 * Copyright: © Capital City Appliance Sales & Service Ltd.
 *
 * Description:
 * This script dynamically updates stock availability messages for product variants on Shopify product pages.
 * It is designed to handle both single-variant and multi-variant products, leveraging the `StockAvailabilityData`
 * object embedded in the page for seamless integration.
 *
 * Features:
 * ----------
 * 1. **Dynamic Availability Messages**:
 *    - Displays availability messages dynamically based on the selected product variant.
 *    - Handles predefined sub-statuses (`REG`, `TNA`, `NLA`, `DLT`, `PRE`, `OBS`) with tailored messaging logic.
 *    - Includes fallback behavior identical to `REG` for any undefined sub-status.
 *
 * 2. **Single-Variant Product Support**:
 *    - Automatically detects and updates messages for single-variant products using hidden input fields.
 *
 * 3. **Inventory-Based Messaging**:
 *    - Adjusts messages based on stock availability (`quantity`) and variant flags (`available`, `on_sale`, `on_clearance`).
 *    - Includes pre-order messaging and expected availability dates for relevant sub-statuses.
 *
 * 4. **Alternative Product Suggestions**:
 *    - Dynamically generates search links for `alternate_skus` provided in the variant data.
 *
 * 5. **Debug Mode**:
 *    - When `DEBUG` is set to `true`:
 *      - Outputs detailed logs to the console for debugging and inspection.
 *      - Adds variant-specific properties (`item_type`, `price_type`, `status`, etc.) to the availability messages for transparency.
 *    - When `DEBUG` is `false`, excludes all debug-related information from the frontend messages.
 *
 * 6. **Future Scalability**:
 *    - Designed for use across multiple parts of a Shopify site (e.g., home page, collection pages, search results, cart, checkout).
 *
 * Prerequisites:
 * --------------
 * - Ensure the product page includes:
 *   1. A `StockAvailabilityData` object in a `<script>` tag, containing variant-specific information:
 *      - `id`, `quantity`, `sub_status`, `item_type`, `price_type`, `expected_availability`, `alternate_skus`, `status`, `on_sale`, `on_clearance`, and `switch`.
 *   2. A `stock-availability-message` placeholder `<div>` for displaying the dynamic messages.
 *
 * Usage:
 * ------
 * 1. Place this script in your Shopify theme's `assets` directory.
 * 2. Add it to your layout or product page template with:
 *    <script src="{{ 'stock-availability-engine.js' | asset_url }}" defer></script>
 * 3. Include the `StockAvailabilityData` snippet for the required JSON object.
 *
 * Debugging:
 * ----------
 * - Set `DEBUG = true` in the script to enable debug mode.
 * - Debug mode outputs detailed logs to the console and renders additional properties in the message for inspection.
 * - Recommended for testing or development purposes only; disable for production.
 *
 * Sub-Status Behavior:
 * --------------------
 * - `REG`: Regular availability messaging.
 * - `TNA`: Temporarily unavailable messaging with pre-order options.
 * - `PRE`: Pre-order messaging for new products.
 * - `NLA`: Messaging for discontinued products with limited stock.
 * - `DLT`: Messaging for delisted products with limited availability.
 * - `OBS`: Messaging for discontinued but orderable products.
 * - Default (fallback): Matches the behavior of `REG` for undefined or unexpected sub-statuses.
 *
 * Maintenance Notes:
 * ------------------
 * - Keep the `DEBUG` flag disabled (`false`) in production environments.
 * - Regularly verify compatibility with Shopify theme updates.
 * - Ensure all relevant metafields are properly configured for the product variants in your Shopify admin.
 */

document.addEventListener("DOMContentLoaded", function () {
  const DEBUG = false; // Toggle this to enable/disable debugging

  /**
   * Logs debug messages to the console if DEBUG is enabled.
   * @param {string} message - The debug message.
   * @param {any} [data] - Additional data to log (optional).
   */
  function logDebug(message, data = null) {
    if (DEBUG) {
      console.group("Debug");
      console.log(message);
      if (data !== null) {
        console.log("Details:", data);
      }
      console.groupEnd();
    }
  }

  /**
   * Formats a date string into MMM-DD-YYYY format.
   * @param {string} dateString - The date string to format.
   * @returns {string} - The formatted date or an empty string if invalid.
   */
  function formatDate(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { month: "long", day: "2-digit", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  }

  // .replace(",", "")

  /**
   * Generates links for alternate SKUs provided as a comma-separated string.
   * @param {string} skus - The comma-separated SKUs.
   * @returns {string} - HTML string with clickable links for each SKU.
   */
  function generateAlternateSkuLinks(skus) {
    return skus
      .split(",")
      .map((sku) => sku.trim())
      .filter((sku) => sku.length > 0)
      .map(
        (sku) =>
          `<a href="/search?q=${sku}" target="_blank" style="color: green;">${sku}</a>`
      )
      .join(", ");
  }

  // Load variant data from StockAvailabilityData
  const variantData = window.StockAvailabilityData?.variants || [];
  logDebug("Loaded Variant Data:", variantData);

  /**
   * Updates the stock availability message based on the selected variant ID.
   * @param {number} variantId - The ID of the selected variant.
   */
  function updateDynamicMessage(variantId) {
    const selectedVariant = variantData.find(
      (variant) => variant.id === parseInt(variantId)
    );
    const messageElement = document.getElementById(
      "stock-availability-message"
    );

    if (!selectedVariant) {
      console.warn("Selected variant not found in variant data:", variantId);
      return;
    }

    if (!messageElement) {
      console.warn("Message element not found in the DOM.");
      return;
    }

    logDebug("Selected Variant Object:", selectedVariant);

    const alternateSkusMessage = selectedVariant.alternate_skus
      ? `<p><strong>Alternatives:</strong> ${generateAlternateSkuLinks(
          selectedVariant.alternate_skus
        )}</p>`
      : "";

    const availabilityInfo = selectedVariant.expected_availability
      ? `Expected availability: ${formatDate(
          selectedVariant.expected_availability
        )}<br>We will email you to confirm the estimated delivery date once we receive your pre-order.`
      : "";

    
      // : "We will email you to confirm the estimated delivery date once we receive your pre-order.";

    const itemTypeMessage = DEBUG
      ? `<p><strong>Item Type:</strong> ${selectedVariant.item_type}</p>`
      : "";

    const priceTypeMessage = DEBUG
      ? `<p><strong>Price Type:</strong> ${selectedVariant.price_type}</p>`
      : "";

    const statusMessage = DEBUG
      ? `<p><strong>Status:</strong> ${selectedVariant.status}</p>`
      : "";

    const switchMessage = DEBUG
      ? `<p><strong>Switch:</strong> ${selectedVariant.switch}</p>`
      : "";
    const saleMessage = DEBUG
      ? `<p><strong>On Sale:</strong> ${selectedVariant.on_sale}</p>`
      : "";
    const clearanceMessage = DEBUG
      ? `<p><strong>On Clearance:</strong> ${selectedVariant.on_clearance}</p>`
      : "";

    let message = "";
    switch (selectedVariant.sub_status) {
      case "REG":
      default: // Fallback matches REG behavior
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">In Stock! Ready to ship now.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">Ships within 10–14 business days.</p>
                       <p>${availabilityInfo}</p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}`;
        break;

      case "TNA":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">In Stock! Order now while supplies last.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">Ships within 10–14 business days.</p>
                       <p>${availabilityInfo}</p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
        break;

      case "PRE":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">In Stock! Order now to be among the first to enjoy this new product.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">Ships within 10–14 business days.</p>
                       <p>${availabilityInfo}</p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}`;
        break;

      case "NLA":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">Only a few left!</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: gray;">This product is no longer available.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
        break;

      case "DLT":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">Limited stock available!</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: gray;">This product is no longer listed in our store.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
        break;

      case "OBS":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">Limited stock available—order now while supplies last.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">Ships within 10–14 business days.</p>
                       <p>${availabilityInfo}</p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
        break;

        case "CUS":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">Temporarily out of stock. Please call us for availability at 1-800-606-7879.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: gray;">Temporarily out of stock. Please call us for availability at 1-800-606-7879.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
        break;

        case "GRI":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">Temporarily out of stock -available Sept 13th, 2025.  Pre-order now.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">Temporarily out of stock -available Sept 13th, 2025.  Pre-order now.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
        break;

        case "WE_KNOW_NOTHING":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">Preorder item — ETA not yet available. We'll ship as soon as it's in stock.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">Preorder item — ETA not yet available. We'll ship as soon as it's in stock.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
        break;

        case "UB": /**Urban Bonfire */
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">Ships within 8–10 weeks.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">Ships within 8–10 weeks.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
        break;

        case "TNA2026":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">This product will be available for order in early 2026.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">This product will be available for order in early 2026.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
        break;

        case "TICKET":
          message =
            selectedVariant.quantity <= 0
              ? `<p><strong>Availability:</strong> <span style="color: gray;">Sold Out — no tickets remaining.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
              : selectedVariant.quantity < 5
              ? `<p><strong>Availability:</strong> <span style="color: orange;">🔥 Hurry! Only <strong>${selectedVariant.quantity}</strong> tickets left — almost gone!</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
              : `<p><strong>Availability:</strong> <span style="color: green;">Tickets available now — don’t wait, secure your spot today!</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
          break;

        case "IN_STOCK":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">In Stock! Ready to ship now.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">In Stock! Ready to ship now.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;
        break;

        case "NLA-CUS":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">2025 Model — Limited Stock.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: gray;">This product is no longer available.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;       
        break;

        case "CUS2":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">In Stock - ships within 48 hours.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">In Stock - ships within 48 hours.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;        
        break;

        case "CUS3":
        message =
          selectedVariant.quantity > 0
            ? `<p><strong>Availability:</strong> <span style="color: green;">In Stock - ships within 5-7 business days.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`
            : `<p><strong>Availability:</strong> <span style="color: green;">In Stock - ships within 5-7 business days.</span></p>${itemTypeMessage}${priceTypeMessage}${statusMessage}${switchMessage}${saleMessage}${clearanceMessage}${alternateSkusMessage}`;        
        break;

    }

    messageElement.innerHTML = message;
  }

  const singleVariantInput = document.querySelector(
    "input.variant-selection__variants--default"
  );

  if (singleVariantInput) {
    const variantId = singleVariantInput.value;
    logDebug("Single Variant Detected. Variant ID:", variantId);
    updateDynamicMessage(variantId);
  } else {
    const dropdownSelector = document.querySelector(
      "select.variant-selection__variants"
    );
    const radioButtons = document.querySelectorAll(
      "input.options-selection__option-value-input"
    );

    if (dropdownSelector && dropdownSelector.style.display !== "none") {
      dropdownSelector.addEventListener("change", function (event) {
        logDebug("Dropdown Selected Value:", event.target.value);
        updateDynamicMessage(event.target.value);
      });

      const defaultVariantId = dropdownSelector.value;
      if (defaultVariantId && defaultVariantId !== "not-selected") {
        logDebug("Default Variant ID (Dropdown):", defaultVariantId);
        updateDynamicMessage(defaultVariantId);
      }
    } else if (radioButtons.length > 0) {
      radioButtons.forEach((button) => {
        button.addEventListener("change", function (event) {
          logDebug("Radio Button Selected Value:", event.target.value);

          const variantId = document
            .querySelector("variant-selection")
            ?.getAttribute("variant");
          if (variantId) {
            updateDynamicMessage(variantId);
          }
        });
      });

      const activeVariant = document
        .querySelector("variant-selection")
        ?.getAttribute("variant");
      if (activeVariant) {
        logDebug("Default Variant ID (Radio):", activeVariant);
        updateDynamicMessage(activeVariant);
      }
    } else {
      console.error("No variant selectors found.");
    }
  }
});
